import { useMutation } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { useAuth } from "@/context/auth";
import { fetchClient } from "@/lib/api-client";

class ApiError extends Error {
  constructor(public readonly status: number) {
    super(`API error ${status}`);
  }
}

/**
 * Hook for dev impersonation flow.
 * Calls POST /auth/impersonate with target wallet address,
 * receives a read-only JWT, and saves it in the auth store.
 */
export function useImpersonate() {
  const t = useTranslations("auth.impersonation");
  const auth = useAuth();

  return useMutation({
    mutationKey: ["impersonate"],
    mutationFn: async (walletAddress: string) => {
      const { data, response } = await fetchClient.POST("/auth/impersonate", {
        body: { wallet_address: walletAddress },
      });

      if (!response.ok) {
        throw new ApiError(response.status);
      }

      const result = data?.data;
      if (!result) {
        throw new Error("No data received from impersonation endpoint");
      }

      return result;
    },
    onSuccess: (data) => {
      auth.startImpersonation(data.access_token, data.user);
      toast.success(
        t("toasts.viewingAs", {
          name: data.user.nickname ?? data.user.wallet_address,
        }),
      );
    },
    onError: (error) => {
      const status = error instanceof ApiError ? error.status : 0;
      if (status === 403) {
        toast.error(t("toasts.accessDenied"));
      } else if (status === 404) {
        toast.error(t("toasts.notFound"));
      } else {
        toast.error(t("toasts.error"));
      }
    },
  });
}
